import React from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import FlightTakeoffOutlinedIcon from '@mui/icons-material/FlightTakeoffOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import AddShoppingCartOutlinedIcon from '@mui/icons-material/AddShoppingCartOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import {
  DOCUMENT,
  DOCUMENT_NAV_LOGO_HEIGHT_MOBILE,
  documentNavLogoSx,
} from '../constants/documentTheme';
import { isUsableImageUrl } from '../utils/itineraryItemImage';
import {
  getPartnerNavKey,
  PARTNER_DESKTOP_RAIL_PRIMARY,
  PARTNER_DESKTOP_RAIL_SECONDARY,
  PARTNER_SIDEBAR_WIDTH,
} from '../utils/partnerWorkspace';
import { developerPublicBase } from '../utils/developerHost';
import { NavMessageBadge } from './NavMessageBadge';
import useInquiryUnreadCount from '../hooks/useInquiryUnreadCount';

const ICONS = {
  dashboard: DashboardOutlinedIcon,
  trips: FlightTakeoffOutlinedIcon,
  clients: PeopleAltOutlinedIcon,
  packages: Inventory2OutlinedIcon,
  inventory: WarehouseOutlinedIcon,
  addons: AddShoppingCartOutlinedIcon,
  requests: ForumOutlinedIcon,
  inquiries: MailOutlineIcon,
  bookings: ConfirmationNumberOutlinedIcon,
  analytics: InsightsOutlinedIcon,
  team: GroupsOutlinedIcon,
  howTo: MenuBookOutlinedIcon,
  profile: PersonOutlineIcon,
  billing: CreditCardOutlinedIcon,
  developer: CodeOutlinedIcon,
};

const PartnerDesktopSidebar = ({
  partner,
  brand,
  entitlements,
  headerBg,
  accent,
  menuInk,
  logoFailed,
  onLogoError,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const activeKey = getPartnerNavKey(location.pathname);
  const inquiryUnread = useInquiryUnreadCount({ enabled: true, viewer: 'partner' });
  const companyLogoUrl = brand?.logoUrl
    || (entitlements?.tier === 'bespoke' && isUsableImageUrl(partner?.logo)
      ? String(partner.logo).trim()
      : null);

  const linkSx = (active) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    px: 1.75,
    py: 1.1,
    mx: 1,
    mb: 0.35,
    textDecoration: 'none',
    color: active ? accent : menuInk,
    bgcolor: active ? 'rgba(255,255,255,0.1)' : 'transparent',
    borderLeft: active ? `2px solid ${accent}` : '2px solid transparent',
    fontFamily: DOCUMENT.sans,
    fontSize: 12,
    fontWeight: 500,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    '&:hover': {
      bgcolor: 'rgba(255,255,255,0.08)',
      color: active ? accent : DOCUMENT.ivory,
    },
  });

  return (
    <Box
      component="aside"
      data-testid="partner-desktop-sidebar"
      sx={{
        display: { xs: 'none', md: 'flex' },
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        width: PARTNER_SIDEBAR_WIDTH,
        zIndex: 25,
        flexDirection: 'column',
        bgcolor: headerBg,
        borderRight: `1px solid ${brand ? 'rgba(255,255,255,0.12)' : DOCUMENT.navRule}`,
      }}
    >
      <Box
        component={RouterLink}
        to="/partner/dashboard"
        aria-label={brand?.name || 'Déjà Vu Nomad'}
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 2,
          textDecoration: 'none',
          minHeight: 72,
          borderBottom: `1px solid ${brand ? 'rgba(255,255,255,0.12)' : DOCUMENT.navRule}`,
        }}
      >
        {companyLogoUrl ? (
          <Box
            component="img"
            src={companyLogoUrl}
            alt={brand?.name || partner?.company_name || ''}
            data-testid="partner-workspace-logo"
            sx={{
              height: 40,
              width: 'auto',
              maxWidth: 180,
              objectFit: 'contain',
              objectPosition: 'left center',
              display: 'block',
            }}
          />
        ) : brand ? (
          <Typography
            sx={{
              fontFamily: DOCUMENT.sans,
              fontSize: 13,
              color: DOCUMENT.ivory,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
            }}
          >
            {brand.name}
          </Typography>
        ) : logoFailed ? (
          <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 18, color: DOCUMENT.ivory }}>
            Déjà Vu Nomad
          </Typography>
        ) : (
          <Box
            component="img"
            src="/noBgWhite.png"
            alt="Déjà Vu Nomad"
            onError={onLogoError}
            sx={{
              ...documentNavLogoSx,
              height: DOCUMENT_NAV_LOGO_HEIGHT_MOBILE,
              maxWidth: 160,
            }}
          />
        )}
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', py: 1.5 }}>
        <Typography
          sx={{
            px: 2.5,
            mb: 1,
            fontSize: 10,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: 'rgba(246,239,228,0.45)',
          }}
        >
          {t('partnerNav.workspace', 'Workspace')}
        </Typography>
        {PARTNER_DESKTOP_RAIL_PRIMARY.map((item) => {
          const Icon = ICONS[item.key];
          const active = item.key === 'packages'
            ? (activeKey === 'packages' || activeKey === 'dmcs')
            : item.key === activeKey;
          const dmcActive = location.pathname.startsWith('/partner/packages/dmcs');
          const iconNode = Icon ? <Icon sx={{ fontSize: 18 }} /> : null;
          return (
            <React.Fragment key={item.key}>
            <Box
              component={RouterLink}
              to={item.to}
              data-testid={`partner-rail-${item.key}`}
              sx={linkSx(active)}
            >
              <Box
                component="span"
                sx={{
                  width: 18,
                  height: 18,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {item.key === 'inquiries' ? (
                  <NavMessageBadge count={inquiryUnread}>
                    {iconNode}
                  </NavMessageBadge>
                ) : iconNode}
              </Box>
              {t(`partnerNav.${item.key}`)}
            </Box>
            {item.key === 'packages' ? (
              <Box
                component={RouterLink}
                to="/partner/packages/dmcs"
                data-testid="partner-rail-dmcs"
                sx={{
                  display: 'block',
                  ml: 5.5,
                  mr: 1,
                  mb: 0.75,
                  pl: 1.25,
                  py: 0.55,
                  textDecoration: 'none',
                  color: dmcActive ? accent : 'rgba(246,239,228,0.72)',
                  fontFamily: DOCUMENT.sans,
                  fontSize: 11,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  borderLeft: dmcActive ? `2px solid ${accent}` : '2px solid transparent',
                  '&:hover': { color: DOCUMENT.ivory },
                }}
              >
                {t('partnerNav.dmcs')}
              </Box>
            ) : null}
            </React.Fragment>
          );
        })}
      </Box>

      <Box sx={{ py: 1.5, borderTop: `1px solid ${brand ? 'rgba(255,255,255,0.12)' : DOCUMENT.navRule}` }}>
        <Typography
          sx={{
            px: 2.5,
            mb: 1,
            fontSize: 10,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: 'rgba(246,239,228,0.45)',
          }}
        >
          {t('partnerNav.account', 'Account')}
        </Typography>
        {PARTNER_DESKTOP_RAIL_SECONDARY.map((item) => {
          const Icon = ICONS[item.key];
          const active = item.key === 'profile'
            ? location.pathname.startsWith('/partner/profile')
            : item.key === 'howTo'
              ? location.pathname.startsWith('/partner/how-to')
              : location.pathname.startsWith('/partner/billing');
          return (
            <Box
              key={item.key}
              component={RouterLink}
              to={item.to}
              data-testid={`partner-rail-${item.key}`}
              sx={linkSx(active)}
            >
              {Icon ? <Icon sx={{ fontSize: 18 }} /> : null}
              {t(item.key === 'billing' ? 'nav.partnerBilling' : item.key === 'howTo' ? 'howTo.menu' : 'partnerDash.profile')}
            </Box>
          );
        })}
        <Box
          component="a"
          href={developerPublicBase()}
          data-testid="partner-rail-developer"
          sx={linkSx(false)}
        >
          <CodeOutlinedIcon sx={{ fontSize: 18 }} />
          {t('partnerDash.developer')}
        </Box>
        <Box
          component="button"
          type="button"
          onClick={() => navigate('/trips/create')}
          data-testid="partner-rail-new-trip"
          sx={{
            ...linkSx(false),
            width: 'calc(100% - 16px)',
            mt: 1,
            border: 0,
            cursor: 'pointer',
            bgcolor: accent,
            color: brand ? '#000000' : DOCUMENT.nav,
            justifyContent: 'center',
            letterSpacing: '0.12em',
            '&:hover': { filter: 'brightness(1.06)', bgcolor: accent },
          }}
        >
          {t('partnerNav.newTrip')}
        </Box>
      </Box>
    </Box>
  );
};

export default PartnerDesktopSidebar;
